/**
 * One Ask Dink answer (DNKADM-0460): the question bubble, the source tag, the answer,
 * the screen buttons, the follow-ups and the thumbs.
 *
 *  - `help`: a blue "From the Dink help guide" tag, the answer, and the help-guide
 *    sections it used (`sources`).
 *  - `unsure`: an amber "Not sure" tag, where Dink looked (the API's one-line answer)
 *    and who can answer: the facility owner for Staff, Dink support for owners. No
 *    contact link or hours: there are none to show yet.
 *  - `other` (a kind this client does not know): the answer with no tag, so it is never
 *    mislabelled as the help guide.
 *
 * Backend text (answer, link labels, sources, suggestions) is brand-scrubbed at render.
 * A link renders only when its path is in the caller's own route list, at most three.
 */
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import type { AssistantAnswerKind, AssistantLink } from '@/api/assistant';
import { IconAction } from '@/components/IconAction';
import { Icon } from '@/theme';
import { scrubBrand } from '@/utils/whitelabel';
import { AnswerMarkdown } from './AnswerMarkdown';

const MAX_LINKS = 3;

export interface AskDinkExchange {
  key: number;
  question: string;
  answer: string;
  links: AssistantLink[];
  questionId: string;
  kind: AssistantAnswerKind;
  sources: string[];
  suggestions: string[];
  feedback: boolean | null;
  /** When the answer arrived (epoch ms), for the history list. */
  answeredAt: number;
}

/** Who the "Not sure" card sends the operator to. */
export type AskDinkAccessRole = 'Staff' | 'Owner';

export function QuestionBubble({ text }: { text: string }) {
  return (
    <Typography
      sx={{
        alignSelf: 'flex-end',
        maxWidth: '85%',
        bgcolor: 'action.selected',
        borderRadius: '16px 16px 4px 16px',
        px: 1.75,
        py: 1.25,
        fontSize: 14,
        fontWeight: 500,
        overflowWrap: 'anywhere',
      }}
    >
      {text}
    </Typography>
  );
}

function SectionLabel({ children }: { children: string }) {
  return (
    <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>
      {children}
    </Typography>
  );
}

function WhoCanAnswer({ role }: { role: AskDinkAccessRole }) {
  const who =
    role === 'Staff'
      ? {
          icon: 'person',
          title: 'Your facility owner',
          detail: 'They decide how things are set up and what the rules are here.',
        }
      : {
          icon: 'support_agent',
          title: 'Dink support',
          detail: 'For questions about how Dink works.',
        };
  return (
    <Stack sx={{ gap: 1 }}>
      <SectionLabel>Who can answer</SectionLabel>
      <Stack
        direction="row"
        sx={{
          gap: 1.5,
          alignItems: 'flex-start',
          p: 1.5,
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        <Icon name={who.icon} sx={{ fontSize: 22, color: 'text.secondary' }} />
        <Box>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{who.title}</Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{who.detail}</Typography>
        </Box>
      </Stack>
    </Stack>
  );
}

export interface AskDinkAnswerProps {
  exchange: AskDinkExchange;
  allowedPaths: ReadonlySet<string>;
  role: AskDinkAccessRole;
  /** False while another question is on its way: follow-ups wait for it. */
  canAsk: boolean;
  onNavigate: (path: string) => void;
  onAsk: (question: string) => void;
  onFeedback: (helpful: boolean) => void;
  /** Copy the answer text; the panel toasts the result. */
  onCopy: () => void;
}

export function AskDinkAnswer({
  exchange,
  allowedPaths,
  role,
  canAsk,
  onNavigate,
  onAsk,
  onFeedback,
  onCopy,
}: AskDinkAnswerProps) {
  const links = exchange.links.filter((link) => allowedPaths.has(link.path)).slice(0, MAX_LINKS);
  const unsure = exchange.kind === 'unsure';

  return (
    <Stack component="section" sx={{ gap: 1.5, mb: 3 }}>
      <QuestionBubble text={exchange.question} />

      {exchange.kind === 'help' ? (
        <Chip
          size="small"
          color="info"
          variant="outlined"
          icon={<Icon name="menu_book" sx={{ fontSize: 16 }} />}
          label="From the Dink help guide"
          sx={{ alignSelf: 'flex-start', fontWeight: 600 }}
        />
      ) : null}
      {unsure ? (
        <Chip
          size="small"
          color="warning"
          variant="outlined"
          icon={<Icon name="help" sx={{ fontSize: 16 }} />}
          label="Not sure"
          sx={{ alignSelf: 'flex-start', fontWeight: 600 }}
        />
      ) : null}

      {unsure ? (
        <>
          <Typography component="h3" sx={{ fontSize: 16, fontWeight: 700 }}>
            {"I don't have a sure answer for this one."}
          </Typography>
          <Stack sx={{ gap: 0.5 }}>
            <SectionLabel>Where Dink looked</SectionLabel>
            <AnswerMarkdown markdown={exchange.answer} />
          </Stack>
          <WhoCanAnswer role={role} />
        </>
      ) : (
        <AnswerMarkdown markdown={exchange.answer} />
      )}

      {exchange.kind === 'help' && exchange.sources.length > 0 ? (
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
          {`Help guide: ${exchange.sources.map(scrubBrand).join(' · ')}`}
        </Typography>
      ) : null}

      {links.length > 0 ? (
        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
          {links.map((link) => (
            <Button
              key={link.path}
              variant="contained"
              size="small"
              endIcon={<Icon name="arrow_forward" sx={{ fontSize: 18 }} />}
              onClick={() => onNavigate(link.path)}
            >
              {scrubBrand(link.label)}
            </Button>
          ))}
        </Stack>
      ) : null}

      {exchange.suggestions.length > 0 ? (
        <Stack sx={{ gap: 1 }}>
          <SectionLabel>{unsure ? 'I can help with' : 'Ask next'}</SectionLabel>
          {exchange.suggestions.map((suggestion) => (
            <Button
              key={suggestion}
              variant="outlined"
              color="inherit"
              disabled={!canAsk}
              endIcon={<Icon name="arrow_forward" sx={{ fontSize: 18 }} />}
              // Ask the scrubbed text: it becomes the question shown in the bubble and history.
              onClick={() => onAsk(scrubBrand(suggestion) ?? suggestion)}
              sx={{ justifyContent: 'space-between', textAlign: 'left', borderColor: 'divider' }}
            >
              {scrubBrand(suggestion)}
            </Button>
          ))}
        </Stack>
      ) : null}

      <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5 }}>
        <Typography sx={{ fontSize: 13, color: 'text.secondary', mr: 0.5 }}>
          Did this help?
        </Typography>
        <IconAction
          label="Helpful"
          aria-pressed={exchange.feedback === true}
          disabled={exchange.feedback !== null}
          onClick={() => onFeedback(true)}
          sx={{ width: 44, height: 44 }}
        >
          <Icon name="thumb_up" sx={{ fontSize: 18 }} />
        </IconAction>
        <IconAction
          label="Not helpful"
          aria-pressed={exchange.feedback === false}
          disabled={exchange.feedback !== null}
          onClick={() => onFeedback(false)}
          sx={{ width: 44, height: 44 }}
        >
          <Icon name="thumb_down" sx={{ fontSize: 18 }} />
        </IconAction>
        <IconAction label="Copy answer" onClick={onCopy} sx={{ width: 44, height: 44 }}>
          <Icon name="content_copy" sx={{ fontSize: 18 }} />
        </IconAction>
        {exchange.feedback !== null ? (
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            Thanks for the feedback.
          </Typography>
        ) : null}
      </Stack>
    </Stack>
  );
}
